/**
 * Chapi and the logo as vector data, from the motion source (docs/designs/motion/Chapi_in_Motion.html),
 * which is drawn from the character kit (brand/chapi) and logo pack (brand/logo). The web renders it with
 * SVG elements and mobile with react-native-svg, so both draw the same character from one file.
 *
 * Chapi is split into the layers the animations move: legs, left arm, right arm, head (with the eyes as
 * their own layer, for blinking) and body. Art colours are the illustration's own and stay the same in
 * light and dark themes, like the poses in brand/chapi.
 */
import { color } from './index.js';
export const chapiPalette = {
    ink: color.ink,
    orange: ['#FF7A3D', '#FFB547'],
    ultraviolet: ['#C04BF2', '#7C3AED', '#2563EB'],
    shoe: '#FFFFFF',
    shoeLine: '#DCE0F2',
    sole: '#C7CCF5',
    laceLeft: color.orange,
    laceRight: '#5B4CF5',
    tongue: '#E5566E',
    cheek: '#FF8FB1',
    mic: color.orange,
    highlight: '#FFFFFF',
    zNear: '#A5B4FC',
    zFar: '#C7D2FE',
};
/** Confetti, in the design's order (the pieces cycle through these). */
export const confettiColors = [color.orange, '#FFB547', color.pink, color.violet, color.blue, '#22C55E'];
const P = chapiPalette;
const line = (d, width) => ({ kind: 'path', d, stroke: P.ink, width, round: true });
/** The Chapsi leaf, 357 by 228 path units: the logo mark is two of them, Chapi's head and body one each. */
export const LEAF_PATH = 'M205 0 H343 Q357 0 355 14 C346 112 268 228 152 228 H14 Q0 228 2 214 C11 116 89 0 205 0 Z';
export const MOUTH_PATH = 'M96.46 62.16 Q106.3 78.66 116.14 62.16 Z';
const hand = (x, y, deg) => ({
    kind: 'group',
    transform: `translate(${x} ${y}) rotate(${deg})`,
    children: [
        { kind: 'ellipse', cx: 0, cy: 0, rx: 8.2, ry: 9.6, fill: P.ink },
        { kind: 'ellipse', cx: -8.2, cy: 3, rx: 3.5, ry: 5.2, transform: 'rotate(-35 -8.2 3)', fill: P.ink },
    ],
});
const shoe = (x, flip, lace) => ({
    kind: 'group',
    transform: `translate(${x} 172) scale(${flip ? -1.25 : 1.25} 1.25)`,
    children: [
        { kind: 'rect', x: -5, y: -12, width: 10, height: 7, rx: 2.5, fill: lace },
        { kind: 'path', d: 'M-12 7 Q-13 -7 0 -7 Q11 -7 17 3 Q19 7 17 8 Z', fill: P.shoe, stroke: P.shoeLine, width: 1.4 },
        { kind: 'rect', x: -13, y: 6, width: 31, height: 5, rx: 2.5, fill: P.sole },
        { kind: 'path', d: 'M1 4 L7 -4', stroke: lace, width: 4, round: true },
    ],
});
const eye = (cx) => [
    { kind: 'ellipse', cx, cy: 51.36, rx: 6.48, ry: 8.4, fill: P.ink },
    { kind: 'circle', cx: cx + 2.16, cy: 48.24, r: 2.4, fill: P.highlight },
];
export const chapiRig = {
    legs: [line('M88 140 L84 168', 7), line('M110 140 L115 168', 7), shoe(80, true, P.laceLeft), shoe(118, false, P.laceRight)],
    arms: {
        /** Left arm out, right arm raised (waving, thinking, popping in, jumping). */
        wave: {
            left: [line('M86.645 106.96 Q55.645 111.96 48.645 128.96', 7), hand(48.645, 128.96, 25)],
            right: [line('M147.665 107.5 Q167.665 83.5 163.665 71.5', 7), hand(163.665, 71.5, 15)],
        },
        /** Both arms resting (asleep). */
        rest: {
            left: [line('M86.645 106.96 Q59.645 114.96 56.645 134.96', 7), hand(56.645, 134.96, 25)],
            right: [line('M147.665 107.5 Q172.665 116.5 173.665 137.5', 7), hand(173.665, 137.5, -20)],
        },
    },
    head: [
        { kind: 'group', transform: 'translate(100 60) scale(0.36) translate(-178.5 -114)', children: [{ kind: 'path', d: LEAF_PATH, fill: 'url:or' }] },
        { kind: 'ellipse', cx: 68.14, cy: 33.36, rx: 15, ry: 5.5, transform: 'rotate(-34 68.14 33.36)', fill: P.highlight, opacity: 0.28 },
    ],
    face: {
        awake: {
            eyes: [...eye(88.3), ...eye(124.3)],
            rest: [
                line('M82.9 37.92 Q88.3 33.36 93.7 37.92', 3.12),
                line('M118.9 37.92 Q124.3 33.36 129.7 37.92', 3.12),
                { kind: 'path', d: MOUTH_PATH, fill: P.ink, stroke: P.ink, width: 1.92, joinRound: true },
                { kind: 'ellipse', cx: 107.5, cy: 72.456, rx: 6.1008, ry: 4.224, fill: P.tongue, clipMouth: true },
            ],
        },
        asleep: {
            eyes: [line('M81.1 51.36 Q88.3 57.36 95.5 51.36', 4.08), line('M117.1 51.36 Q124.3 57.36 131.5 51.36', 4.08)],
            rest: [{ kind: 'ellipse', cx: 108.7, cy: 65.76, rx: 3.36, ry: 3.84, fill: P.ink }],
        },
        /** Worried (task delayed, off sick): eyes open, brows up in the middle, a small frown. */
        worried: {
            rest: [
                line('M82.9 37.5 Q88.6 36.4 94 32.2', 3.12),
                line('M118.6 32.2 Q124 36.4 129.7 37.5', 3.12),
                { kind: 'path', d: 'M98.5 70 Q106.3 63.5 114 70', stroke: P.ink, width: 3.2, round: true },
            ],
        },
        /** Off sick: flushed cheeks (they pulse). */
        flushed: [
            { kind: 'ellipse', cx: 77.5, cy: 60.96, rx: 5.28, ry: 3.36, fill: '#FF4D6D', opacity: 0.7 },
            { kind: 'ellipse', cx: 135.1, cy: 60.96, rx: 5.28, ry: 3.36, fill: '#FF4D6D', opacity: 0.7 },
        ],
        cheeks: [
            { kind: 'ellipse', cx: 77.5, cy: 60.96, rx: 5.28, ry: 3.36, fill: P.cheek, opacity: 0.55 },
            { kind: 'ellipse', cx: 135.1, cy: 60.96, rx: 5.28, ry: 3.36, fill: P.cheek, opacity: 0.55 },
        ],
    },
    headset: [
        line('M125.74 19.96 Q155.18 -1.04 166.18 21.76', 4.6),
        { kind: 'rect', x: 156.68, y: 19.26, width: 13, height: 21, rx: 6.5, fill: P.ink },
        { kind: 'rect', x: 159.68, y: 23.26, width: 7, height: 13, rx: 3.5, fill: P.mic },
        line('M163.18 39.76 Q161.18 62.4 122.3 71.04', 3.4),
        { kind: 'ellipse', cx: 120.3, cy: 71.04, rx: 5, ry: 4, fill: P.ink },
    ],
    body: [
        { kind: 'group', transform: 'translate(110 121) scale(0.27) translate(-178.5 -114)', children: [{ kind: 'path', d: LEAF_PATH, fill: 'url:uv' }] },
        { kind: 'ellipse', cx: 129.305, cy: 101.02, rx: 16, ry: 4, transform: 'rotate(-18 129.305 101.02)', fill: P.highlight, opacity: 0.18 },
    ],
    /** The small z's drawn into the sleeping pose (the animated ones float above them). */
    sleepZs: [
        { kind: 'text', x: 160, y: 22, size: 18, fill: P.zNear, text: 'z' },
        { kind: 'text', x: 172, y: 8, size: 13, fill: P.zFar, text: 'z' },
        { kind: 'text', x: 182, y: -4, size: 10, fill: P.zFar, text: 'z' },
    ],
};
/**
 * Where each layer turns, in art units: the CSS transform-origin of each layer's box in the design.
 * Arms turn at the shoulder, the head a little below its middle, the whole figure at its feet.
 */
export const chapiPivots = {
    whole: { x: 104.2, y: 185.75 },
    head: { x: 100, y: 68.2 },
    armL: { x: 86.645, y: 106.96 },
    armR: { x: 147.665, y: 107.5 },
    /** Both eyes share a centre line, so one layer blinks them both. */
    eyes: { x: 106.3, y: 51.36 },
};
/** Off sick: the blanket over the knees and the ice pack on the head (drawn in art units). */
export const sickProps = {
    blanket: { x: 28.9, y: 127.4, width: 145, height: 57.2, rx: 18 },
    blanketStripes: ['#C4B5FD', '#DDD6FE'],
    icepack: { x: 72.1, y: 9.4, width: 59.6, height: 30.8, rx: 15, rotate: -14, colors: ['#E0F2FE', '#7DD3FC'] },
    drip: '#7DD3FC',
    /** The thermometer in the temperature bubble (14 by 48). */
    thermometer: { glass: '#FFFFFF', line: '#94A3B8', mercury: '#E11D48' },
};
const holidayHand = (x, y, deg) => hand(x, y, deg);
/** On holiday: sunglasses, an ice cream, the headset and a little sun, as one drawing that sways. */
export const holidayArt = {
    viewBox: { x: 27.85, y: -11, width: 181.15, height: 202.75 },
    shapes: [
        line('M88 140 L84 168', 7), line('M110 140 L115 168', 7), shoe(80, true, P.laceLeft), shoe(118, false, P.laceRight),
        line('M86.645 106.96 Q56.645 112.96 50.645 130.96', 7), holidayHand(50.645, 130.96, 25),
        line('M147.665 107.5 Q170.665 104.5 169.665 113.5', 7), holidayHand(169.665, 113.5, -10),
        { kind: 'group', transform: 'translate(100 60) scale(0.36) translate(-178.5 -114)', children: [{ kind: 'path', d: LEAF_PATH, fill: 'url:or' }] },
        { kind: 'ellipse', cx: 68.14, cy: 33.36, rx: 15, ry: 5.5, transform: 'rotate(-34 68.14 33.36)', fill: P.highlight, opacity: 0.28 },
        line('M82.9 37.92 Q88.3 33.36 93.7 37.92', 3.12),
        line('M118.9 37.92 Q124.3 33.36 129.7 37.92', 3.12),
        { kind: 'path', d: MOUTH_PATH, fill: P.ink, stroke: P.ink, width: 1.92, joinRound: true },
        { kind: 'ellipse', cx: 107.5, cy: 72.456, rx: 6.1008, ry: 4.224, fill: P.tongue, clipMouth: true },
        { kind: 'ellipse', cx: 77.5, cy: 60.96, rx: 5.28, ry: 3.36, fill: P.cheek, opacity: 0.55 },
        { kind: 'ellipse', cx: 135.1, cy: 60.96, rx: 5.28, ry: 3.36, fill: P.cheek, opacity: 0.55 },
        { kind: 'path', d: 'M77.3 43.36 H101.3 Q101.3 61.36 89.3 61.36 Q77.3 61.36 77.3 43.36 Z', fill: '#1E2550' },
        { kind: 'path', d: 'M82.3 47.36 L87.3 47.36', stroke: '#FFFFFF', width: 2, round: true, opacity: 0.6 },
        { kind: 'path', d: 'M111.3 43.36 H135.3 Q135.3 61.36 123.3 61.36 Q111.3 61.36 111.3 43.36 Z', fill: '#1E2550' },
        { kind: 'path', d: 'M116.3 47.36 L121.3 47.36', stroke: '#FFFFFF', width: 2, round: true, opacity: 0.6 },
        { kind: 'path', d: 'M101.3 45.36 H111.3', stroke: '#1E2550', width: 3, round: true },
        { kind: 'group', transform: 'translate(110 121) scale(0.27) translate(-178.5 -114)', children: [{ kind: 'path', d: LEAF_PATH, fill: 'url:uv' }] },
        { kind: 'ellipse', cx: 129.305, cy: 101.02, rx: 16, ry: 4, transform: 'rotate(-18 129.305 101.02)', fill: P.highlight, opacity: 0.18 },
        {
            kind: 'group',
            transform: 'translate(160 112) scale(1.3)',
            children: [
                { kind: 'path', d: 'M-10 -2 L0 26 L10 -2 Z', fill: '#F4C27A' },
                { kind: 'path', d: 'M-6 4 L4 0 M-4 12 L6 6', stroke: '#D99A4E', width: 1.8, round: true },
                { kind: 'circle', cx: -4, cy: -8, r: 9, fill: '#FF8FB1' },
                { kind: 'circle', cx: 5, cy: -10, r: 9, fill: '#C4B5FD' },
                { kind: 'circle', cx: 1, cy: -18, r: 7, fill: '#FFE4A8' },
            ],
        },
        line('M125.74 19.96 Q155.18 -1.04 166.18 21.76', 4.6),
        { kind: 'rect', x: 156.68, y: 19.26, width: 13, height: 21, rx: 6.5, fill: P.ink },
        { kind: 'rect', x: 159.68, y: 23.26, width: 7, height: 13, rx: 3.5, fill: P.mic },
        line('M163.18 39.76 Q161.18 62.4 122.3 71.04', 3.4),
        { kind: 'ellipse', cx: 120.3, cy: 71.04, rx: 5, ry: 4, fill: P.ink },
    ],
    /** The small sun by the hat (centre and rays), which turns. */
    sun: { cx: 182, cy: 16, r: 11, color: '#FFCD00', rays: [0, 45, 90, 135, 180, 225, 270, 315] },
    /** Sun, waves and sun cream from the design's scene. */
    sky: { sun: '#FFB547', waveNear: '#38BDF8', waveFar: '#7DD3FC' },
};
const BIRTHDAY_MOUTH = 'M94.9 62.16 Q106.3 81.66 117.7 62.16 Z';
/** Birthday: party hat, a cake in both hands and a little confetti. Built for a future birthday nudge. */
export const birthdayArt = {
    viewBox: { x: 6.54, y: -58, width: 195.02, height: 249.75 },
    mouth: BIRTHDAY_MOUTH,
    shapes: [
        line('M88 140 L84 168', 7), line('M110 140 L115 168', 7), shoe(80, true, P.laceLeft), shoe(118, false, P.laceRight),
        { kind: 'group', transform: 'translate(100 60) scale(0.36) translate(-178.5 -114)', children: [{ kind: 'path', d: LEAF_PATH, fill: 'url:or' }] },
        { kind: 'ellipse', cx: 68.14, cy: 33.36, rx: 15, ry: 5.5, transform: 'rotate(-34 68.14 33.36)', fill: P.highlight, opacity: 0.28 },
        line('M81.1 53.76 Q88.3 42.96 95.5 53.76', 4.56), line('M82.9 37.92 Q88.3 33.36 93.7 37.92', 3.12),
        line('M117.1 53.76 Q124.3 42.96 131.5 53.76', 4.56), line('M118.9 37.92 Q124.3 33.36 129.7 37.92', 3.12),
        { kind: 'path', d: BIRTHDAY_MOUTH, fill: P.ink, stroke: P.ink, width: 1.92, joinRound: true },
        { kind: 'ellipse', cx: 107.5, cy: 74.328, rx: 7.068, ry: 4.992, fill: P.tongue, clipMouth: true },
        { kind: 'ellipse', cx: 77.5, cy: 60.96, rx: 5.28, ry: 3.36, fill: P.cheek, opacity: 0.55 },
        { kind: 'ellipse', cx: 135.1, cy: 60.96, rx: 5.28, ry: 3.36, fill: P.cheek, opacity: 0.55 },
        { kind: 'group', transform: 'translate(110 121) scale(0.27) translate(-178.5 -114)', children: [{ kind: 'path', d: LEAF_PATH, fill: 'url:uv' }] },
        { kind: 'ellipse', cx: 129.305, cy: 101.02, rx: 16, ry: 4, transform: 'rotate(-18 129.305 101.02)', fill: P.highlight, opacity: 0.18 },
        {
            kind: 'group',
            transform: 'translate(102 128) scale(1.35)',
            children: [
                { kind: 'rect', x: -22, y: -6, width: 44, height: 22, rx: 5, fill: '#FFFFFF' },
                { kind: 'path', d: 'M-22 0 Q-16 6 -11 0 Q-5 6 0 0 Q5 6 11 0 Q16 6 22 0 V-4 H-22 Z', fill: '#FF8FB1' },
                { kind: 'rect', x: -2, y: -20, width: 4, height: 14, rx: 1.5, fill: color.violet },
                { kind: 'path', d: 'M0 -29 Q4 -24 0 -21 Q-4 -24 0 -29 Z', fill: '#FFB547' },
            ],
        },
        line('M86.645 106.96 Q73.3225 118.48 80 142', 7), hand(80, 142, -70),
        line('M147.665 107.5 Q121.8325 117.75 124 140', 7), hand(124, 140, 70),
        { kind: 'circle', cx: 159.58, cy: 33.36, r: 6.5, fill: P.ink },
        { kind: 'circle', cx: 159.58, cy: 33.36, r: 3, fill: P.mic },
        line('M159.58 38.36 Q157.58 61.7 122.3 71.04', 3.4),
        { kind: 'ellipse', cx: 120.3, cy: 71.04, rx: 5, ry: 4, fill: P.ink },
        {
            kind: 'group',
            transform: 'translate(135.1 22.96) scale(1.35) translate(-135.1 -22.96) translate(135.1 20.96) rotate(10)',
            children: [
                { kind: 'path', d: 'M-17 0 L0 -44 L17 0 Z', fill: color.violet },
                { kind: 'path', d: 'M-10 -18 L10 -18 M-13 -8 L13 -8 M-6 -30 L6 -30', stroke: '#FFB547', width: 3, round: true },
                { kind: 'circle', cx: 0, cy: -46, r: 5, fill: '#FF8FB1' },
                { kind: 'rect', x: -19, y: -3, width: 38, height: 5, rx: 2.5, fill: '#FFB547' },
            ],
        },
        ...[
            [24, 22, 20, '#FFB547'], [40, 8, -30, '#C04BF2'], [170, 10, 40, '#FF7A3D'], [186, 40, -10, '#2563EB'], [16, 60, 60, '#22C55E'], [184, 82, 15, '#FF8FB1'],
        ].map(([x, y, r, fill]) => ({ kind: 'group', transform: `rotate(${r} ${x} ${y})`, children: [{ kind: 'rect', x, y, width: 9, height: 4, rx: 2, fill }] })),
    ],
    balloons: ['#F472B6', '#A78BFA', '#FFB547'],
};
export const chapiViewBox = {
    /** Waving, thinking, popping in, jumping (no headset). */
    plain: { x: 25.85, y: 12.96, width: 154.22, height: 178.79 },
    /** With the headset, which rises above the head. */
    headset: { x: 25.85, y: 3.89, width: 154.22, height: 187.86 },
    /** Asleep: arms out and the drawn z's. */
    asleep: { x: 30.37, y: -18.8, width: 162.07, height: 210.55 },
};
export const viewBoxAttr = (v) => `${v.x} ${v.y} ${v.width} ${v.height}`;
/** A point in the view box as percentages of the drawing, for transform-origin on a full-size layer. */
export const pivotPercent = (v, p) => ({ x: ((p.x - v.x) / v.width) * 100, y: ((p.y - v.y) / v.height) * 100 });
/** The logo (4098 by 1306): two leaves, the "chapsi" wordmark and the dot over the i. */
export const logoArt = {
    viewBox: { x: 0, y: 0, width: 4098, height: 1306 },
    /** The leaves sit in `translate(40 40) scale(3.08)`; the second leaf is offset by (20, 170) inside it. */
    mark: { x: 40, y: 40, scale: 3.0801507537688444, offsetB: { x: 20, y: 170 } },
    /** The wordmark's glyphs, drawn at `translate(1352.097 933.74)`. */
    word: {
        x: 1352.0968341708544,
        y: 933.74,
        /** The glyphs' box in logo units, for the wipe's clip window. */
        box: { x: 1379, y: 200, width: 2647, height: 1000 },
        d: 'M287 14Q245 14 199.5 -2.5Q154 -19 114.5 -51.5Q75 -84 51.0 -132.5Q27 -181 27 -245Q27 -308 51.0 -356.0Q75 -404 114.0 -436.0Q153 -468 198.5 -484.5Q244 -501 285 -501Q323 -501 351.5 -492.5Q380 -484 399.0 -474.0Q418 -464 427 -457Q445 -445 461.0 -430.5Q477 -416 476 -392Q476 -379 470.5 -366.5Q465 -354 455 -339Q421 -289 388 -291Q372 -292 360.0 -298.0Q348 -304 337.5 -311.0Q327 -318 314.5 -323.5Q302 -329 285 -329Q262 -329 242.5 -318.0Q223 -307 211.5 -288.0Q200 -269 200 -244Q200 -220 211.5 -200.5Q223 -181 242.0 -169.5Q261 -158 283 -158Q298 -158 309.0 -161.5Q320 -165 326.5 -169.0Q333 -173 336 -175Q349 -183 361.0 -189.5Q373 -196 385 -196Q401 -196 418.0 -184.5Q435 -173 455 -147Q474 -123 476.5 -102.5Q479 -82 466.0 -63.5Q453 -45 427 -28Q422 -25 403.5 -14.5Q385 -4 355.5 5.0Q326 14 287 14Z M590 9Q548 9 530.0 -3.5Q512 -16 509.0 -35.5Q506 -55 506 -76V-640Q506 -662 509.5 -681.0Q513 -700 531.0 -712.0Q549 -724 591 -724Q632 -724 649.5 -711.5Q667 -699 671.0 -680.0Q675 -661 675 -639V-448Q691 -464 717.5 -479.5Q744 -495 780 -495Q844 -495 894.0 -462.5Q944 -430 974.0 -373.5Q1004 -317 1004 -246V-75Q1004 -54 1000.0 -35.0Q996 -16 978.5 -3.5Q961 9 919 9Q878 9 860.0 -3.0Q842 -15 838.0 -34.5Q834 -54 834 -77V-247Q834 -272 825.0 -289.5Q816 -307 799.5 -317.0Q783 -327 759 -327Q728 -327 703.5 -308.0Q679 -289 675 -262V-75Q675 -54 671.0 -34.5Q667 -15 649.5 -3.0Q632 9 590 9Z M1444 7Q1402 7 1382.0 -7.0Q1362 -21 1363 -40L1376 -47Q1370 -39 1354.5 -26.0Q1339 -13 1314.0 -3.0Q1289 7 1252 7Q1206 7 1164.5 -13.0Q1123 -33 1091.0 -69.0Q1059 -105 1040.0 -150.0Q1021 -195 1021 -245Q1021 -294 1040.0 -339.0Q1059 -384 1091.5 -419.0Q1124 -454 1165.0 -474.5Q1206 -495 1252 -495Q1287 -495 1311.5 -484.5Q1336 -474 1351.5 -461.5Q1367 -449 1374 -439L1364 -444Q1363 -469 1383.0 -482.5Q1403 -496 1445 -496Q1487 -496 1504.0 -483.5Q1521 -471 1525.0 -451.0Q1529 -431 1529 -410V-77Q1529 -56 1525.0 -36.5Q1521 -17 1504.0 -5.0Q1487 7 1444 7ZM1275 -161Q1297 -161 1315.0 -172.5Q1333 -184 1344.0 -203.0Q1355 -222 1355 -244Q1355 -268 1344.0 -286.5Q1333 -305 1314.5 -316.5Q1296 -328 1273 -328Q1250 -328 1231.0 -316.5Q1212 -305 1200.5 -286.0Q1189 -267 1189 -243Q1189 -221 1201.0 -202.5Q1213 -184 1232.5 -172.5Q1252 -161 1275 -161Z M1833 -2Q1784 -2 1747.5 -21.5Q1711 -41 1687.5 -75.5Q1664 -110 1652.5 -154.0Q1641 -198 1641 -247Q1642 -296 1654.0 -341.0Q1666 -386 1690.0 -420.5Q1714 -455 1749.5 -475.0Q1785 -495 1833 -495Q1877 -495 1918.5 -474.5Q1960 -454 1992.5 -419.0Q2025 -384 2044.0 -339.0Q2063 -294 2063 -245Q2063 -195 2044.0 -150.5Q2025 -106 1993.0 -72.5Q1961 -39 1919.5 -20.5Q1878 -2 1833 -2ZM1645 219Q1604 219 1586.5 207.0Q1569 195 1565.5 176.5Q1562 158 1562 137V-412Q1562 -433 1565.5 -452.0Q1569 -471 1586.0 -483.5Q1603 -496 1644 -496Q1677 -496 1698.5 -486.5Q1720 -477 1729 -458V136Q1731 157 1726.5 176.0Q1722 195 1704.5 207.0Q1687 219 1645 219ZM1810 -160Q1833 -160 1852.0 -171.5Q1871 -183 1882.5 -202.0Q1894 -221 1894 -244Q1894 -267 1883.0 -286.0Q1872 -305 1853.0 -316.0Q1834 -327 1811 -327Q1789 -327 1770.0 -315.5Q1751 -304 1740.0 -285.5Q1729 -267 1729 -244Q1729 -221 1740.5 -202.0Q1752 -183 1770.5 -171.5Q1789 -160 1810 -160Z M2267 18Q2239 18 2205.5 11.5Q2172 5 2141.0 -7.0Q2110 -19 2089.0 -35.5Q2068 -52 2067 -73Q2066 -83 2070.0 -95.0Q2074 -107 2081.5 -121.0Q2089 -135 2100 -151Q2108 -161 2117.5 -165.5Q2127 -170 2140 -168Q2157 -167 2174.0 -159.5Q2191 -152 2207.5 -143.0Q2224 -134 2242.5 -127.0Q2261 -120 2281 -120Q2304 -120 2316.0 -125.5Q2328 -131 2328 -142Q2328 -151 2321.5 -157.0Q2315 -163 2303.0 -167.0Q2291 -171 2276.0 -174.5Q2261 -178 2244.0 -182.5Q2227 -187 2209 -192Q2185 -199 2162.5 -210.5Q2140 -222 2122.0 -240.5Q2104 -259 2093.5 -285.5Q2083 -312 2083 -350Q2083 -401 2106.5 -435.0Q2130 -469 2176.0 -487.0Q2222 -505 2288 -505Q2306 -505 2323.5 -502.5Q2341 -500 2358.5 -496.0Q2376 -492 2393.0 -485.0Q2410 -478 2428 -470Q2462 -454 2463.0 -427.5Q2464 -401 2444 -372Q2432 -354 2419.5 -345.5Q2407 -337 2395 -338Q2379 -339 2360.0 -348.0Q2341 -357 2320.0 -366.0Q2299 -375 2278 -375Q2265 -375 2255.0 -371.5Q2245 -368 2240.0 -362.5Q2235 -357 2235 -349Q2235 -337 2242.5 -330.0Q2250 -323 2263.0 -319.0Q2276 -315 2292.5 -311.5Q2309 -308 2328 -305Q2355 -300 2382.5 -291.5Q2410 -283 2433.5 -267.0Q2457 -251 2471.0 -223.5Q2485 -196 2485 -151Q2485 -69 2429.5 -25.5Q2374 18 2267 18Z M2590 9Q2548 9 2530.0 -3.5Q2512 -16 2509.0 -35.5Q2506 -55 2506 -76V-411Q2506 -432 2509.5 -451.0Q2513 -470 2531.0 -482.5Q2549 -495 2591 -495Q2632 -495 2649.5 -482.5Q2667 -470 2670.5 -450.5Q2674 -431 2674 -409V-75Q2674 -54 2670.5 -34.5Q2667 -15 2649.5 -3.0Q2632 9 2590 9Z',
    },
    /** The dot over the i, in the wordmark's coordinates. */
    dot: { cx: 2590, cy: -693, r: 118, gradient: ['#FFB547', '#FF7A3D'] },
};
/** The loading mark: the two leaves alone (417 by 438). */
export const markArt = { viewBox: { x: 0, y: 0, width: 417, height: 438 }, offset: { x: 20, y: 20 }, offsetB: { x: 20, y: 170 } };
//# sourceMappingURL=chapi-art.js.map